import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { CreanceRow } from "@/lib/creance.api";
import { getTotalDue } from "../creance-calculations";
import {
  formatDate,
  formatMoney,
  getCreanceInvoiceLabel,
  parseAmount,
} from "../creance-formatters";
import { CreanceStatusBadge } from "./CreanceStatusBadge";

interface CreanceDetailDialogProps {
  creance: CreanceRow | null;
  onOpenChange: (open: boolean) => void;
}

export function CreanceDetailDialog({ creance, onOpenChange }: CreanceDetailDialogProps) {
  return (
    <Dialog open={Boolean(creance)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-3xl">
        {creance && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    Créance - échéance #{creance.sequence}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    {creance.partyName} - {getCreanceInvoiceLabel(creance).toLowerCase()}
                  </DialogDescription>
                </div>
                <CreanceStatusBadge creance={creance} />
              </div>
            </DialogHeader>

            <div className="grid gap-3 sm:grid-cols-4">
              <DetailStat label="Date prévue" value={formatDate(creance.expectedDate)} />
              <DetailStat
                label="Montant attendu"
                value={formatMoney(parseAmount(creance.expectedAmount))}
              />
              <DetailStat label="Total dû" value={formatMoney(getTotalDue(creance))} />
              <DetailStat label="Client" value={`#${creance.partyId}`} />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-border">
                <div className="border-b border-border bg-surface-muted px-4 py-2">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Principal
                  </p>
                </div>
                <DetailRows
                  rows={[
                    ["Montant attendu", formatMoney(parseAmount(creance.expectedAmount))],
                    ["Principal payé", formatMoney(parseAmount(creance.principalPaid))],
                    ["Principal dû", formatMoney(parseAmount(creance.principalDue))],
                  ]}
                />
              </div>

              <div className="overflow-hidden rounded-lg border border-border">
                <div className="border-b border-border bg-surface-muted px-4 py-2">
                  <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Pénalités
                  </p>
                </div>
                <DetailRows
                  rows={[
                    ["Pénalité accumulée", formatMoney(parseAmount(creance.penaltyAccrued))],
                    ["Pénalité payée", formatMoney(parseAmount(creance.penaltyPaid))],
                    ["Pénalité due", formatMoney(parseAmount(creance.penaltyDue))],
                  ]}
                />
              </div>
            </div>

            <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                Facture
              </p>
              <Link
                to={`/invoices/${creance.invoiceId}`}
                className="mt-1 inline-flex text-[12.5px] font-medium text-brand-gold-dark hover:underline"
              >
                Ouvrir {getCreanceInvoiceLabel(creance)}
              </Link>
            </div>

            <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              <Link to={`/invoices/${creance.invoiceId}`}>
                <Button>Voir la facture</Button>
              </Link>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-card px-3 py-2">
      <p className="text-[10.5px] font-semibold uppercase tracking-wide text-subtle">{label}</p>
      <p className="mt-1 truncate text-[12px] font-medium text-body">{value}</p>
    </div>
  );
}

function DetailRows({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-left">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label} className="border-b border-border last:border-0">
            <td className="px-4 py-2 text-[12px] text-muted-foreground">{label}</td>
            <td className="px-4 py-2 text-right text-[12px] font-medium text-body">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
